/**
 * 图片格式运行时增强
 *
 * - applyFormatActivation: 将 <picture source data-srcset> 激活为 srcset（ok Cookie 确认后）
 *   供 JS bundle 加载后处理 header/footer 等动态渲染内容
 *
 * - applyForceFallback: forceFallback / ns Cookie 模式下，移除 <picture> 中的 AVIF/WebP <source>
 *
 * - getBestCdnUrl: 辅助工具，基于 localStorage 缓存同步返回最优格式 URL
 *   供非 <img> 场景使用（如 JS 构建 CSS background-image 等）
 *
 * 注：静态 HTML 中的 CDN <img> 已由 scripts/transform-cdn-to-picture.js 在构建时
 *     转换为 <picture>（<source> 使用 data-srcset，由 headerContent.html 内联脚本在运行时激活）。
 */

import { isForceFallbackActive, getAvifSupportSync, getWebpSupportSync, markAvifSupported, markWebpSupported, AVIF_OK_COOKIE, WEBP_OK_COOKIE } from './formatDetect';
import { CDN_AVIF_PATHS, CDN_WEBP_PATHS } from './cdn-optimized-paths.generated';

function readOkCookie(name: string): boolean {
    try {
        return new RegExp(`(?:^|;\\s*)${name}=1(?:;|$)`).test(document.cookie);
    } catch {
        return false;
    }
}

function activateSource(source: HTMLSourceElement, avifOk: boolean, webpOk: boolean): void {
    const ds = source.getAttribute('data-srcset');
    if (!ds) return;
    if (avifOk && source.type === 'image/avif') source.setAttribute('srcset', ds);
    else if (webpOk && source.type === 'image/webp') source.setAttribute('srcset', ds);
}

/**
 * 激活页面中已有及后续动态插入的 <picture source data-srcset> 元素。
 * 供 JS bundle 加载后处理 header/footer 等动态渲染内容（inline script 的 MutationObserver 在 DOMContentLoaded 后已断开）。
 */
export function applyFormatActivation(): void {
    if (isForceFallbackActive()) return;
    const avifOk = readOkCookie(AVIF_OK_COOKIE);
    const webpOk = readOkCookie(WEBP_OK_COOKIE);
    if (!avifOk && !webpOk) return;

    document.querySelectorAll<HTMLSourceElement>('picture source').forEach((s) => activateSource(s, avifOk, webpOk));

    const ob = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            Array.from(mutation.addedNodes).forEach((node) => {
                if (node.nodeType !== 1) return;
                const el = node as Element;
                if (el.tagName === 'SOURCE' && el.parentElement?.tagName === 'PICTURE') {
                    activateSource(el as HTMLSourceElement, avifOk, webpOk);
                } else if (el.querySelectorAll) {
                    Array.from(el.querySelectorAll<HTMLSourceElement>('picture source')).forEach((s) => activateSource(s, avifOk, webpOk));
                }
            });
        }
    });
    ob.observe(document.body ?? document.documentElement, { childList: true, subtree: true });
}

const CDN_DOMAINS = new Set([
    'statics-cdn-cn.umucdn.cn',
    'd1bvk99i2a79wx.cloudfront.net',
    'dmks9efzatfsk.cloudfront.net',
    'dpfrzbu0cqnmm.cloudfront.net',
    'd1yemi1ss2f2oh.cloudfront.net',
]);

function isCdnRasterUrl(url: string): boolean {
    if (!url) return false;
    try {
        const { hostname, pathname } = new URL(url);
        return (
            CDN_DOMAINS.has(hostname.toLowerCase()) &&
            /\.(png|jpe?g)(\?.*)?$/i.test(pathname)
        );
    } catch {
        return false;
    }
}

function replaceExt(url: string, newExt: string): string {
    return url.replace(/\.(png|jpe?g)(\?.*)?$/i, `${newExt}$2`);
}

function getPathStem(url: string): string {
    try {
        return new URL(url).pathname.replace(/\.(png|jpe?g)(\?.*)?$/i, '');
    } catch {
        return '';
    }
}

function readNsCookie(name: string): boolean {
    try {
        return new RegExp(`(?:^|;\\s*)${name}=1(?:;|$)`).test(document.cookie);
    } catch {
        return false;
    }
}

/**
 * 移除页面中 <picture> 里被拦截格式的 <source>（兜底，处理 JS bundle 执行后动态插入的内容）：
 * - forceFallback 激活时移除全部 AVIF/WebP
 * - Cookie umu_avif_ns=1 时移除 AVIF，umu_webp_ns=1 时移除 WebP
 * 静态 HTML 中的 webpack source（srcset）和 CDN source（data-srcset）
 * 已由 headerContent.html 内联脚本在解析阶段处理。
 */
export function applyForceFallback(): void {
    const ff = isForceFallbackActive();
    const rmAvif = ff || readNsCookie('umu_avif_ns');
    const rmWebp = ff || readNsCookie('umu_webp_ns');
    if (!rmAvif && !rmWebp) return;
    document.querySelectorAll<HTMLSourceElement>('picture source').forEach((source) => {
        if (rmAvif && source.type === 'image/avif') source.remove();
        else if (rmWebp && source.type === 'image/webp') source.remove();
    });
}

/**
 * 监听 <picture> 内 <img> 的加载事件，通过 currentSrc 正向确认实际加载的格式，
 * 调用 markAvifSupported() / markWebpSupported() 清除"不支持"Cookie。
 */
export function watchPictureImgLoad(): void {
    document.querySelectorAll<HTMLImageElement>('picture img').forEach((img) => {
        if (img.complete && img.currentSrc) {
            onPictureLoad(img.currentSrc);
        } else {
            img.addEventListener('load', () => onPictureLoad(img.currentSrc), { once: true });
        }
    });
}

function onPictureLoad(src: string): void {
    if (!src) return;
    if (src.includes('.avif')) markAvifSupported();
    else if (src.includes('.webp')) markWebpSupported();
}

/**
 * 返回当前最优格式的 CDN URL（基于 localStorage 同步缓存）。
 * 适用于非 <img> 场景（JS 动态设置 CSS background-image 等）。
 * 常规 <img> 场景请在静态 HTML 中使用 <picture> 元素（由 transform-cdn-to-picture.js 生成）。
 */
export function getBestCdnUrl(url: string): string {
    if (!isCdnRasterUrl(url)) return url;
    if (isForceFallbackActive()) return url;
    const stem = getPathStem(url);
    if (getAvifSupportSync() === true && CDN_AVIF_PATHS.has(stem)) return replaceExt(url, '.avif');
    if (getWebpSupportSync() === true && CDN_WEBP_PATHS.has(stem)) return replaceExt(url, '.webp');
    return url;
}
